import {
  Add,
  Check,
  DeleteOutlined,
  Error,
  ExpandMore,
  FileDownloadOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import grey from "@mui/material/colors/grey";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import {
  BasicItem,
  Configuration,
  ExtendedFieldError,
  MfaPolicy,
  Profile,
  Project,
} from "product-test-types";
import { useMemo, useState } from "react";

import { downloadUsersExcelExport } from "../../helpers/fetch-excel-export";
import { SeatInfo } from "../../helpers/fetch-settings";
import { theme } from "../../theme";
import {
  getItemName,
  getPluralOfItemName,
} from "../../utils/get-plural-of-item-name";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../utils/get-plural-of-project-name";
import {
  ControlledSelectField,
  ControlledTextFormField,
  FormFieldWrapper,
  usePimoForm,
} from "../pimo-form";
import {
  computeSeatUsage,
  generateAccessString,
  getAccessOptions,
  handleAccessChange,
  handleProjectIdsChange,
} from "./manage-users.helpers";

type ManageUserRow = Profile & {
  rowKey?: string;
};

type MfaChipConfig = {
  color: "default" | "success" | "warning";
  label: string;
};

type ManageUsersPageProps = {
  configuration?: Configuration;
  language: SupportedLanguage;
  currentUserEmail?: string;
  currentUserName?: string;
};

export const ManageUsersPage: PimoReactComponent<ManageUsersPageProps> = ({
  language,
  currentUserEmail,
  currentUserName,
}) => {
  const { setValue, formState } = usePimoForm<{
    basicItems: BasicItem[];
    configuration?: Configuration;
    projects: Project[];
    validationErrors?: Record<string, ExtendedFieldError>;
    clientUsers?: Profile[];
    toolUsers?: ManageUserRow[];
    seatInfo?: SeatInfo | null;
    mfaPolicy?: MfaPolicy;
    enableSaveButton?: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const [openDelete, setOpenDelete] = useState(false);
  const [currentSelectedItem, setCurrentSelectedItem] = useState<
    string[] | undefined
  >(undefined);

  const handleAppend = () => {
    if (atCapacity) {
      return;
    }
    const newItem: ManageUserRow = {
      id: crypto.randomUUID(),
      rowKey: crypto.randomUUID(),
      groups: [""],
      email: "",
      name: "",
      permissions: {
        role: undefined,
        client: formState.configuration?.client ?? "",
        tool: formState.configuration?.tool ?? "",
        product: "pimo-product",
      },
      flag: "add",
    };

    setValue("toolUsers", [...(formState?.toolUsers ?? []), newItem]);
    if (formState.enableSaveButton !== true) {
      setValue("enableSaveButton", true);
    }

    setValue("hasUpdatedUsers", true);
    const element = document.getElementById("dummy-to-scroll-into");
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const handleDelete = (indices: string[]) => {
    setCurrentSelectedItem(indices);
    setOpenDelete(true);
  };
  const handleConfirmDelete = () => {
    if (currentSelectedItem === undefined) {
      return;
    }

    const updatedUsers = formState?.toolUsers?.map((user) => {
      if (currentSelectedItem.includes(user.id ?? "")) {
        return { ...user, flag: "delete" };
      }
      return user;
    });

    setValue("toolUsers", updatedUsers);
    if (formState.enableSaveButton !== true) {
      setValue("enableSaveButton", true);
    }
    setOpenDelete(false);
    setCurrentSelectedItem(undefined);

    setValue("hasUpdatedUsers", true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setCurrentSelectedItem(undefined);
  };

  const activeUserIds =
    formState.toolUsers?.filter((u) => u.flag !== "delete").map((u) => u.id) ??
    [];

  const seatInfo = formState.seatInfo;
  const { seatsUsed, seatLimit, seatsRemaining, atCapacity } = useMemo(
    () =>
      computeSeatUsage({
        seatInfo,
        clientUsers: formState.clientUsers,
        toolUsers: formState.toolUsers,
      }),
    [seatInfo, formState.clientUsers, formState.toolUsers]
  );

  const seatUsageLabel = seatInfo
    ? getLocalizedMessage(
        language,
        seatInfo.planName
          ? "manageUsers.seatUsage"
          : "manageUsers.seatUsageFreeTier",
        {
          plan: seatInfo.planName ?? "",
          used: seatsUsed,
          limit: seatLimit ?? "",
          remaining: seatsRemaining ?? "",
        }
      )
    : "";

  // The signed-in user always occupies a seat (counted once across all tools).
  // Show them in the roster even when they aren't a member of this tool.
  const normalizedCurrentEmail = currentUserEmail?.trim().toLowerCase();
  const displayedToolUsers =
    formState?.toolUsers?.filter((user) => user.flag !== "delete") ?? [];
  const showMfaPolicyHint = formState.mfaPolicy === "totp-required";
  const isCurrentUserListed = normalizedCurrentEmail
    ? displayedToolUsers.some(
        (u) => u.email?.trim().toLowerCase() === normalizedCurrentEmail
      )
    : false;
  const isCurrentUser = (email?: string) =>
    Boolean(
      normalizedCurrentEmail &&
      email?.trim().toLowerCase() === normalizedCurrentEmail
    );

  const projectName = {
    single: getProjectName(formState?.configuration?.projectName, language),
    plural: getPluralOfProjectName(
      formState?.configuration?.projectName,
      language
    ),
  };
  const itemName = {
    single: getItemName(formState?.configuration?.itemName, language),
    plural: getPluralOfItemName(formState?.configuration?.itemName, language),
  };

  const getNextUserFlag = (currentFlag?: Profile["flag"], value?: string) => {
    if (value === "__invite__") {
      return "create";
    }

    if (currentFlag === "add" || currentFlag === "create") {
      return currentFlag;
    }

    return "update";
  };

  const getMfaChipConfig = (
    mfaStatus?: Profile["mfaStatus"]
  ): MfaChipConfig | undefined => {
    switch (mfaStatus) {
      case "not-required":
        return {
          color: "default",
          label: getLocalizedMessage(language, "manageUsers.mfa.notRequired"),
        };
      case "required-not-configured":
        return {
          color: "warning",
          label: getLocalizedMessage(
            language,
            "manageUsers.mfa.requiredNotConfigured"
          ),
        };
      case "configured":
        return {
          color: "success",
          label: getLocalizedMessage(language, "manageUsers.mfa.configured"),
        };
      case "pending-invite":
        return {
          color: "default",
          label: getLocalizedMessage(language, "manageUsers.mfa.pendingInvite"),
        };
      default:
        return undefined;
    }
  };

  const getUserFieldError = (index: number, fieldPath: string) => {
    return (
      formState.validationErrors?.[`toolUsers.${index}.${fieldPath}`] ??
      formState.validationErrors?.[`users.${index}.${fieldPath}`]
    );
  };

  const hasUserRowError = (index: number) => {
    return Object.keys(formState?.validationErrors ?? {}).some(
      (item) =>
        item.startsWith(`toolUsers.${index}.`) ||
        item.startsWith(`users.${index}.`)
    );
  };

  const clearUserFieldErrors = (index: number, fieldPaths: string[]) => {
    const newErrors = {
      ...(formState.validationErrors ?? {}),
    } as Record<string, ExtendedFieldError>;

    fieldPaths.forEach((fieldPath) => {
      delete newErrors[`toolUsers.${index}.${fieldPath}`];
      delete newErrors[`users.${index}.${fieldPath}`];
    });

    setValue("validationErrors", newErrors);
  };

  const getUserIndexById = (userId: string) => {
    return formState.toolUsers?.findIndex((user) => user.id === userId) ?? -1;
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography sx={{ fontSize: "24px", fontWeight: 500 }}>
        {getLocalizedMessage(language, "manageUsers.title")}
      </Typography>

      {seatInfo && (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: atCapacity ? theme.palette.error.main : grey[700],
          }}
        >
          {seatUsageLabel}
        </Typography>
      )}
      {showMfaPolicyHint && (
        <Typography
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "14px",
          }}
        >
          {getLocalizedMessage(language, "manageUsers.mfa.policyHint")}
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          mt: 2,
        }}
      >
        <Typography
          sx={{
            color: "#000",
            fontSize: "16px",
            fontWeight: 500,
            width: "100%",
            margin: "auto",
          }}
        >
          {getLocalizedMessage(language, "manageUsers.usersList")}
        </Typography>
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
          }}
        >
          <Grid2
            container
            alignItems="center"
            sx={{
              py: 2,
              paddingLeft: 2,
              borderBottom: "1px solid",
              borderColor: grey[300],
            }}
          >
            <Grid2 size={0.5}></Grid2>
            <Grid2 size={8.5} sx={{ fontSize: "14px", fontWeight: 500 }}>
              {getLocalizedMessage(language, "manageUsers.user")}
            </Grid2>
            <Grid2
              size={3}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                px: 2,
              }}
            >
              {Boolean(currentSelectedItem?.length) && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleDelete(currentSelectedItem ?? []);
                  }}
                  sx={{
                    textTransform: "none",
                    borderRadius: "4px",
                    padding: "7px 16px",
                    whiteSpace: "nowrap",
                    minWidth: "fit-content",
                  }}
                >
                  Delete Selected
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => {
                  downloadUsersExcelExport();
                }}
                startIcon={<FileDownloadOutlined />}
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  padding: "7px 16px",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
              >
                {getLocalizedMessage(language, "manageUsers.export")}
              </Button>
              <Button
                variant="outlined"
                onClick={handleAppend}
                startIcon={<Add />}
                disabled={atCapacity}
                title={
                  atCapacity
                    ? getLocalizedMessage(
                        language,
                        "manageUsers.seatLimitReached"
                      )
                    : undefined
                }
                sx={{
                  textTransform: "none",
                  borderRadius: "4px",
                  padding: "7px 16px",
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
              >
                {getLocalizedMessage(language, "addUser")}
              </Button>
            </Grid2>
          </Grid2>

          <Box
            sx={{
              overflowY: "auto",
              width: "100%",
            }}
          >
            {normalizedCurrentEmail && !isCurrentUserListed && (
              <Grid2
                container
                alignItems="center"
                sx={{
                  width: "100%",
                  py: 1.5,
                  px: 2,
                  borderBottom: "1px solid",
                  borderColor: grey[200],
                }}
              >
                <Grid2 size={0.5}></Grid2>
                <Grid2 sx={{ fontSize: "14px" }} size={8.5}>
                  {currentUserName
                    ? `${currentUserName} (${currentUserEmail})`
                    : currentUserEmail}
                </Grid2>
                <Grid2 size={3} sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    label={getLocalizedMessage(language, "manageUsers.you")}
                    size="small"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      backgroundColor: "#EBE3F4",
                    }}
                  />
                  <Chip
                    label={getLocalizedMessage(language, "manageUsers.active")}
                    size="small"
                    sx={{
                      color: "#008336",
                      fontWeight: 500,
                      backgroundColor: "#e6f9e6",
                    }}
                  />
                </Grid2>
              </Grid2>
            )}
            {formState?.toolUsers
              ?.filter((user) => user.flag !== "delete")
              ?.sort(
                (a, b) =>
                  Number(isCurrentUser(b.email)) -
                  Number(isCurrentUser(a.email))
              )
              ?.map((field) => {
                const index = getUserIndexById(field.id);

                const isSelf = isCurrentUser(field.email);
                const mfaChip = getMfaChipConfig(field.mfaStatus);

                const isUnsavedRow =
                  field.flag === "add" || field.flag === "create";

                return (
                  <Accordion
                    sx={{
                      boxShadow: "none",
                      "&:last-of-type": {
                        borderRadius: "12px",
                      },
                    }}
                    key={field.rowKey ?? field.id}
                    defaultExpanded={isUnsavedRow}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <Grid2
                        container
                        alignItems="center"
                        sx={{ width: "100%", flexWrap: "nowrap" }}
                        key={field.id}
                      >
                        <Grid2 size={0.5}>
                          <Checkbox
                            disabled={isSelf}
                            checked={
                              currentSelectedItem?.includes(field.id ?? "") ??
                              false
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setCurrentSelectedItem((prev) =>
                                isChecked
                                  ? [...(prev ?? []), field.id ?? ""]
                                  : (prev ?? []).filter((i) => i !== field.id)
                              );
                            }}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor: isSelf ? grey[400] : grey[700],

                                  display: "inline-block",
                                  backgroundColor: isSelf ? grey[100] : "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor: isSelf ? grey[400] : grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: isSelf ? grey[100] : "#fff",
                                }}
                              >
                                <Check
                                  style={{
                                    fontSize: 18,
                                    color: isSelf ? grey[400] : grey[700],
                                  }}
                                />
                              </span>
                            }
                          />
                        </Grid2>
                        <Grid2
                          sx={{
                            fontSize: "14px",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                          }}
                          size="grow"
                        >
                          {field.email}
                          {isCurrentUser(field.email) && (
                            <Chip
                              label={getLocalizedMessage(
                                language,
                                "manageUsers.you"
                              )}
                              size="small"
                              sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                                backgroundColor: "#EBE3F4",
                              }}
                            />
                          )}
                        </Grid2>
                        <Grid2
                          sx={{ fontSize: "14px", minWidth: "max-content" }}
                          size="auto"
                        >
                          {field.name ? (
                            <Chip
                              label={"Active"}
                              sx={{
                                color: "#008336",
                                fontWeight: 500,
                                backgroundColor: "#e6f9e6",
                              }}
                            ></Chip>
                          ) : (
                            // "Invitation pending" only makes sense once the invite has
                            // actually been sent - not while the row is still a local draft.
                            !isUnsavedRow && (
                              <Chip
                                label={getLocalizedMessage(
                                  language,
                                  "manageUsers.pending"
                                )}
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontWeight: 500,
                                  backgroundColor: "#EBE3F4",
                                }}
                              ></Chip>
                            )
                          )}
                          {mfaChip && (
                            <Chip color={mfaChip.color} label={mfaChip.label} />
                          )}
                        </Grid2>

                        <Grid2 sx={{ fontSize: "14px" }} size={0.5}>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={isSelf}
                            onClick={() => handleDelete([field.id ?? 0])}
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </Grid2>
                        {hasUserRowError(index) && (
                          <Grid2
                            sx={{
                              fontSize: "14px",
                              color: theme.palette.error.main,
                            }}
                            size={0.5}
                          >
                            <Error />
                          </Grid2>
                        )}
                      </Grid2>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        inert={isSelf}
                        sx={{
                          flexGrow: 1,
                          border: "1px solid",
                          borderColor: grey[300],
                          p: 2,
                          borderRadius: "12px",
                          opacity: isSelf ? 0.5 : 1,
                        }}
                      >
                        <Grid2 container alignItems="center" spacing={2}>
                          <Grid2 size={6}>
                            <FormFieldWrapper label={`Name`}>
                              <ControlledSelectField
                                value={
                                  field.flag === "create"
                                    ? "__invite__"
                                    : (field?.id ?? "")
                                }
                                options={
                                  // Only offer "invite a brand-new user" when
                                  // there are seats left (or this row is already
                                  // a pending invite).
                                  (!atCapacity || field.flag === "create"
                                    ? [
                                        {
                                          label: getLocalizedMessage(
                                            language,
                                            "manageUsers.inviteUsersToPimo"
                                          ),
                                          value: "__invite__",
                                        },
                                      ]
                                    : []
                                  ).concat(
                                    formState.clientUsers
                                      ?.filter(
                                        (user) =>
                                          (user.name || user.email) &&
                                          (!activeUserIds.includes(user.id) ||
                                            user.id === field.id)
                                      )
                                      .map((user) => ({
                                        label: user?.name ?? user?.email ?? "",
                                        value: user?.id ?? "",
                                      })) ?? []
                                  )
                                }
                                onChange={(val) => {
                                  setValue(
                                    `toolUsers.${index}.rowKey`,
                                    field.rowKey ??
                                      field.id ??
                                      crypto.randomUUID()
                                  );

                                  if (val === "__invite__") {
                                    setValue(
                                      `toolUsers.${index}.id`,
                                      crypto.randomUUID()
                                    );
                                    setValue(`toolUsers.${index}.name`, "");
                                    setValue(`toolUsers.${index}.email`, "");
                                    setValue(
                                      `toolUsers.${index}.flag`,
                                      "create"
                                    );
                                    setValue(`toolUsers.${index}.permissions`, {
                                      role: undefined,
                                      client:
                                        formState.configuration?.client ?? "",
                                      tool: formState.configuration?.tool ?? "",
                                      product: "pimo-product",
                                      granularPermissions: [],
                                      projectIds: undefined,
                                      accessLevel: undefined,
                                    });
                                    if (formState.enableSaveButton !== true) {
                                      setValue("enableSaveButton", true);
                                    }
                                    setValue("hasUpdatedUsers", true);
                                    return;
                                  }

                                  // Remove any previously deleted entry with the same id to avoid duplicates
                                  const filteredUsers = (
                                    formState.toolUsers ?? []
                                  ).filter(
                                    (u) =>
                                      !(u.id === val && u.flag === "delete")
                                  );
                                  if (
                                    filteredUsers.length !==
                                    (formState.toolUsers ?? []).length
                                  ) {
                                    setValue("toolUsers", filteredUsers);
                                  }

                                  const updatedIndex = filteredUsers.findIndex(
                                    (u) => u.id === field.id
                                  );
                                  const targetIndex =
                                    updatedIndex !== -1 ? updatedIndex : index;

                                  setValue(
                                    `toolUsers.${targetIndex}.email`,
                                    formState.clientUsers?.find(
                                      (u) => u.id === val
                                    )?.email ?? ""
                                  );
                                  setValue(
                                    `toolUsers.${targetIndex}.name`,
                                    formState.clientUsers?.find(
                                      (u) => u.id === val
                                    )?.name ?? ""
                                  );
                                  setValue(`toolUsers.${targetIndex}.id`, val);
                                  setValue(
                                    `toolUsers.${targetIndex}.flag`,
                                    formState.toolUsers?.find(
                                      (u) => u.id === val
                                    )
                                      ? "update"
                                      : "add"
                                  );
                                  clearUserFieldErrors(targetIndex, [
                                    "email",
                                    "permissions.role",
                                  ]);
                                  if (formState.enableSaveButton !== true) {
                                    setValue("enableSaveButton", true);
                                  }
                                  setValue("hasUpdatedUsers", true);
                                }}
                              />
                            </FormFieldWrapper>
                          </Grid2>

                          <Grid2 size={6}>
                            <FormFieldWrapper
                              label={getLocalizedMessage(
                                language,
                                "manageUsers.email"
                              )}
                            >
                              <ControlledTextFormField
                                value={field.email ?? ""}
                                disabled={field.flag !== "create"}
                                onChange={(e) => {
                                  setValue(`toolUsers.${index}.email`, e);
                                  setValue(
                                    `toolUsers.${index}.flag`,
                                    getNextUserFlag(field.flag)
                                  );
                                  clearUserFieldErrors(index, ["email"]);
                                  if (formState.enableSaveButton !== true) {
                                    setValue("enableSaveButton", true);
                                  }
                                  setValue("hasUpdatedUsers", true);
                                }}
                                error={
                                  getUserFieldError(index, "email") !==
                                  undefined
                                }
                                helperText={
                                  getUserFieldError(index, "email")?.message
                                }
                              />
                            </FormFieldWrapper>
                          </Grid2>

                          <Grid2 size={6}>
                            <FormFieldWrapper
                              label={getLocalizedMessage(
                                language,
                                "manageUsers.role"
                              )}
                            >
                              <ControlledSelectField
                                options={
                                  [
                                    { name: "admin", label: "Admin" },
                                    { name: "program", label: "Program" },
                                    {
                                      name: "project",
                                      label: projectName.single,
                                    },
                                  ]?.map((category) => ({
                                    label: category.label,
                                    value: category.name,
                                  })) ?? []
                                }
                                value={field.permissions?.role ?? ""}
                                onChange={(val) => {
                                  setValue(
                                    `toolUsers.${index}.permissions.role`,
                                    val
                                  );
                                  if (val === "admin") {
                                    setValue(
                                      `toolUsers.${index}.permissions.accessLevel`,
                                      undefined
                                    );
                                    setValue(
                                      `toolUsers.${index}.permissions.projectIds`,
                                      undefined
                                    );
                                  } else if (val === "program") {
                                    setValue(
                                      `toolUsers.${index}.permissions.projectIds`,
                                      undefined
                                    );
                                  }
                                  setValue(
                                    `toolUsers.${index}.flag`,
                                    getNextUserFlag(field.flag)
                                  );
                                  clearUserFieldErrors(index, [
                                    "permissions.role",
                                  ]);
                                  if (formState.enableSaveButton !== true) {
                                    setValue("enableSaveButton", true);
                                  }

                                  setValue("hasUpdatedUsers", true);
                                }}
                                error={
                                  getUserFieldError(
                                    index,
                                    "permissions.role"
                                  ) !== undefined
                                }
                                helperText={
                                  getUserFieldError(index, "permissions.role")
                                    ?.message
                                }
                              />
                            </FormFieldWrapper>
                          </Grid2>
                          {field.permissions?.role !== "admin" && (
                            <Grid2 size={6}>
                              <FormFieldWrapper
                                label={getLocalizedMessage(
                                  language,
                                  "manageUsers.access"
                                )}
                              >
                                <ControlledSelectField
                                  value={
                                    generateAccessString(
                                      field.permissions?.granularPermissions ??
                                        []
                                    ) ?? ""
                                  }
                                  options={
                                    getAccessOptions(
                                      field.permissions?.role ?? "",
                                      projectName.plural,
                                      itemName.plural,
                                      language
                                    ).map((category) => ({
                                      label: category.label,
                                      value: category.name,
                                    })) ?? []
                                  }
                                  onChange={(val) => {
                                    handleAccessChange({
                                      setValue,
                                      index,
                                      val: Array.isArray(val)
                                        ? (val[0] ?? "")
                                        : val,
                                      formState,
                                      currentFlag: field.flag,
                                    });
                                    if (formState.enableSaveButton !== true) {
                                      setValue("enableSaveButton", true);
                                    }
                                    setValue("hasUpdatedUsers", true);
                                  }}
                                />
                              </FormFieldWrapper>
                            </Grid2>
                          )}
                          {field.permissions?.role === "project" && (
                            <Grid2 size={6}>
                              <FormFieldWrapper
                                label={`${projectName.single}`}
                                uiOptions={{ narrowInfoSlotIcon: true }}
                              >
                                <ControlledSelectField
                                  value={field.permissions?.projectIds ?? []}
                                  multiple={true}
                                  options={
                                    formState.projects
                                      ?.filter((project) => project.name)
                                      .map((project) => ({
                                        label: project?.name ?? "",
                                        value: project?.id ?? "",
                                      })) ?? []
                                  }
                                  onChange={(value) => {
                                    handleProjectIdsChange({
                                      setValue,
                                      index,
                                      value,
                                      formState,
                                      currentFlag: field.flag,
                                    });
                                    if (formState.enableSaveButton !== true) {
                                      setValue("enableSaveButton", true);
                                    }

                                    setValue("hasUpdatedUsers", true);
                                  }}
                                />
                              </FormFieldWrapper>
                            </Grid2>
                          )}
                        </Grid2>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            {(formState?.basicItems?.length ?? 0) > 6 && (
              <Box sx={{ height: 40 }} id={"dummy-to-scroll-into"}></Box>
            )}
          </Box>
        </Paper>
        <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
          <Button
            variant="outlined"
            onClick={handleAppend}
            startIcon={<Add />}
            disabled={atCapacity}
            title={
              atCapacity
                ? getLocalizedMessage(language, "manageUsers.seatLimitReached")
                : undefined
            }
            sx={{
              textTransform: "none",
              borderRadius: "4px",
              padding: "7px 16px",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
            }}
          >
            {getLocalizedMessage(language, "addUser")}
          </Button>
        </Box>
      </Box>
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={handleCloseDelete}>
        <DialogTitle>
          {getLocalizedMessage(language, "manageUsers.confirmDelete")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {getLocalizedMessage(language, "manageUsers.confirmDeleteMessage")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>
            {getLocalizedMessage(language, "manageUsers.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>{" "}
          *
        </DialogActions>
      </Dialog>
    </Box>
  );
};
